import { makeAutoObservable, runInAction } from "mobx";
import { IPublicMasterService } from "../PublicMasterProfile/PublicMasterProfileService";
import { CreateBookingRequestService } from "./BookingRequestService";
import { YandexMetrika } from "../../AnalyticsClient/YandexMetrika";

type BookingRequestField = "serviceId" | "clientName" | "contact" | "preferredTime";

export class CreateBookingRequestStore {
  public MasterId: number | null = null;

  public Services: IPublicMasterService[] = [];
  public ServiceId = "";

  public ClientName = "";
  public Contact = "";
  public PreferredTime = "";
  public Comment = "";

  public IsLoading = false;
  public IsCreating = false;
  public Error = "";
  public SuccessMessage = "";

  public FieldErrors: Partial<Record<BookingRequestField, string>> = {};

  private LoadedMasterId: number | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public async load(masterId: number) {
    if (this.IsLoading && this.MasterId === masterId) return;
    if (this.LoadedMasterId === masterId && this.Services.length > 0) return;

    this.MasterId = masterId;
    this.IsLoading = true;
    this.Error = "";

    try {
      const services = await CreateBookingRequestService.GetMasterServices(masterId);

      runInAction(() => {
        this.Services = (services ?? []).filter((service) => service.isActive);
        this.LoadedMasterId = masterId;
      });
    } catch {
      runInAction(() => {
        this.Error = "Не удалось загрузить услуги";
      });
    } finally {
      runInAction(() => {
        this.IsLoading = false;
      });
    }
  }

  private clearFieldError(field: BookingRequestField) {
    if (!this.FieldErrors[field]) return;

    this.FieldErrors = {
      ...this.FieldErrors,
      [field]: undefined,
    };
  }

  public setServiceId(value: string) {
    this.ServiceId = value;
    this.clearFieldError("serviceId");
  }

  public setClientName(value: string) {
    this.ClientName = value;
    this.clearFieldError("clientName");
  }

  public setContact(value: string) {
    this.Contact = value;
    this.clearFieldError("contact");
  }

  public setPreferredTime(value: string) {
    this.PreferredTime = value;
    this.clearFieldError("preferredTime");
  }

  public setComment(value: string) {
    this.Comment = value;
  }

  private validate() {
    const errors: Partial<Record<BookingRequestField, string>> = {};

    if (!Number(this.ServiceId)) {
      errors.serviceId = "Выберите услугу";
    }

    if (!this.ClientName.trim()) {
      errors.clientName = "Введите имя";
    }

    if (!this.Contact.trim()) {
      errors.contact = "Введите Telegram или телефон";
    }

    if (!this.PreferredTime.trim()) {
      errors.preferredTime = "Напишите, когда вам удобно";
    }

    this.FieldErrors = errors;

    return Object.keys(errors).length === 0;
  }

  public async create(onSuccess?: () => void) {
    this.Error = "";
    this.SuccessMessage = "";

    if (!this.MasterId) {
      this.Error = "Мастер не найден";
      return;
    }

    if (!this.validate()) {
      return;
    }

    this.IsCreating = true;

    try {
      await CreateBookingRequestService.Create(this.MasterId, {
        serviceId: Number(this.ServiceId),
        clientName: this.ClientName.trim(),
        contact: this.Contact.trim(),
        preferredTime: this.PreferredTime.trim(),
        comment: this.Comment.trim(),
      });

      YandexMetrika.reachGoal("booking_request_created");

      runInAction(() => {
        this.IsCreating = false;
        this.SuccessMessage = "Заявка отправлена";
      });

      onSuccess?.();
    } catch {
      runInAction(() => {
        this.Error = "Не удалось отправить заявку";
        this.IsCreating = false;
      });
    }
  }
}
